import type { Impersonation } from "@/lib/api";
import { useEffect, useState } from "react";

/**
 * Non-dismissible banner shown to a customer while a Wardkey staff operator is
 * acting on their behalf (WARD-47). There is deliberately no close button and
 * no dismiss state — the only way it goes away is the supervised session
 * ending. It counts down to that moment and reloads at zero (the impersonation
 * session is gone server-side, so the next /bff/me is a normal session again).
 */
export function ImpersonationBanner({ impersonation }: { impersonation: Impersonation }) {
  const [left, setLeft] = useState(() => Math.max(0, impersonation.expires_at - Date.now()));

  useEffect(() => {
    const tick = () => {
      const next = Math.max(0, impersonation.expires_at - Date.now());
      setLeft(next);
      if (next <= 0) window.location.reload();
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [impersonation.expires_at]);

  const mins = Math.floor(left / 60000);
  const secs = Math.floor((left % 60000) / 1000);

  return (
    <div
      role="alert"
      className="sticky top-0 z-50 border-b border-[#FFB85C]/40 bg-[#FFB85C]/15 backdrop-blur"
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-center gap-2 px-4 py-2 text-center">
        <span aria-hidden className="text-[#FFB85C]">
          ●
        </span>
        <span className="text-[13px] text-foreground">
          Wardkey staff <strong className="font-medium">@{impersonation.staff_email}</strong> is
          acting on your behalf — ends in{" "}
          <span className="font-mono tabular-nums">
            {mins}:{String(secs).padStart(2, "0")}
          </span>
        </span>
      </div>
    </div>
  );
}
